import React, { useState, useEffect } from 'react';
import { notificationService } from '../../services/notificationService';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    Eye,
    Trash2,
    Filter,
    Download,
    Calendar,
    Users,
    BarChart3,
    Bell,
    Send
} from 'lucide-react';
import './NotificationHistory.scss';

const NotificationHistory = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        type: '',
        importance: '',
        status: '',
        date_from: '',
        date_to: ''
    });
    const [stats, setStats] = useState(null);
    const { t } = useLanguage();

    useEffect(() => {
        loadNotifications();
        loadStats();
    }, [currentPage, filters]);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const response = await notificationService.getAllNotifications(currentPage, 20, filters);

            if (response.success) {
                setNotifications(response.data.notifications || []);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error(t('loadNotificationsError'), error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await notificationService.getNotificationStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error(t('loadStatsError'), error);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleDelete = async (notificationId) => {
        if (window.confirm(t('deleteNotificationConfirm'))) {
            try {
                await notificationService.deleteNotification(notificationId);
                loadNotifications();
                loadStats();
            } catch (error) {
                console.error(t('deleteNotificationError'), error);
            }
        }
    };

    const handleExport = async () => {
        try {
            const csvContent = convertToCSV(notifications);
            downloadCSV(csvContent, `notifications_${new Date().getTime()}.csv`);
        } catch (error) {
            console.error(t('exportError'), error);
        }
    };

    const convertToCSV = (data) => {
        const headers = ['ID', 'Title', 'Type', 'Importance', 'Target', 'Sent At', 'Sent Count', 'Status'];
        const rows = data.map(item => [
            item.id,
            `"${item.title}"`,
            item.type,
            item.importance,
            item.target_audience,
            item.sent_at || 'Scheduled',
            item.sent_count || 0,
            item.sent_at ? 'Sent' : 'Scheduled'
        ]);

        return [headers, ...rows].map(row => row.join(',')).join('\n');
    };

    const downloadCSV = (content, filename) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('scheduled');
        return new Date(dateString).toLocaleString('tr-TR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (notification) => {
        if (!notification.sent_at) return 'scheduled';
        return 'sent';
    };

    const getDeliveryRate = (notification) => {
        if (!notification.sent_count) return '0%';
        return '~95%';
    };

    if (loading && !notifications.length) {
        return (
            <div className="notification-history">
                <div className="notification-history__loading">
                    <div className="notification-history__spinner"></div>
                    <p>{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notification-history">
            <div className="page-header">
                <h1>{t('notificationHistory')}</h1>
                <p>{t('notificationHistoryDescription')}</p>
            </div>

            {/* İstatistik Kartları */}
            {stats && (
                <div className="notification-stats">
                    <div className="notification-stat notification-stat--total">
                        <div className="notification-stat__icon">
                            <BarChart3 size={24} />
                        </div>
                        <div className="notification-stat__content">
                            <div className="notification-stat__value">{stats.totalNotifications}</div>
                            <div className="notification-stat__label">{t('totalNotifications')}</div>
                        </div>
                    </div>

                    <div className="notification-stat notification-stat--sent">
                        <div className="notification-stat__icon">
                            <Send size={24} />
                        </div>
                        <div className="notification-stat__content">
                            <div className="notification-stat__value">{stats.sentNotifications}</div>
                            <div className="notification-stat__label">{t('sentNotifications')}</div>
                        </div>
                    </div>

                    <div className="notification-stat notification-stat--scheduled">
                        <div className="notification-stat__icon">
                            <Calendar size={24} />
                        </div>
                        <div className="notification-stat__content">
                            <div className="notification-stat__value">{stats.scheduledNotifications}</div>
                            <div className="notification-stat__label">{t('scheduledNotifications')}</div>
                        </div>
                    </div>

                    <div className="notification-stat notification-stat--recipients">
                        <div className="notification-stat__icon">
                            <Users size={24} />
                        </div>
                        <div className="notification-stat__content">
                            <div className="notification-stat__value">{stats.totalRecipients}</div>
                            <div className="notification-stat__label">{t('totalRecipients')}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filtreler */}
            <div className="notification-filters">
                <div className="notification-filters__header">
                    <h3>
                        <Filter size={20} />
                        {t('filters')}
                    </h3>
                    <button className="notification-filters__export" onClick={handleExport}>
                        <Download size={16} />
                        {t('exportCSV')}
                    </button>
                </div>

                <div className="notification-filters__controls">
                    <select
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                        <option value="">{t('allTypes')}</option>
                        <option value="info">{t('info')}</option>
                        <option value="warning">{t('warning')}</option>
                        <option value="security">{t('security')}</option>
                        <option value="update">{t('update')}</option>
                        <option value="system">{t('system')}</option>
                    </select>

                    <select
                        value={filters.importance}
                        onChange={(e) => handleFilterChange('importance', e.target.value)}
                    >
                        <option value="">{t('allImportance')}</option>
                        <option value="low">{t('low')}</option>
                        <option value="medium">{t('medium')}</option>
                        <option value="high">{t('high')}</option>
                        <option value="critical">{t('critical')}</option>
                    </select>

                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <option value="">{t('allStatus')}</option>
                        <option value="sent">{t('sent')}</option>
                        <option value="scheduled">{t('scheduled')}</option>
                    </select>

                    <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        placeholder={t('startDate')}
                    />

                    <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        placeholder={t('endDate')}
                    />

                    <button
                        className="notification-filters__clear"
                        onClick={() => setFilters({
                            type: '',
                            importance: '',
                            status: '',
                            date_from: '',
                            date_to: ''
                        })}
                    >
                        {t('clearFilters')}
                    </button>
                </div>
            </div>

            {/* Bildirim Tablosu */}
            <div className="notification-table">
                <table className="notification-table__content">
                    <thead>
                        <tr>
                            <th>{t('title')}</th>
                            <th>{t('type')}</th>
                            <th>{t('importance')}</th>
                            <th>{t('target')}</th>
                            <th>{t('sentAt')}</th>
                            <th>{t('sentCount')}</th>
                            <th>{t('deliveryRate')}</th>
                            <th>{t('status')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {notifications.map(notification => (
                            <tr key={notification.id}>
                                <td>
                                    <div className="notification-table__title">
                                        <strong>{notification.title}</strong>
                                        <small>{notification.message.substring(0, 60)}...</small>
                                    </div>
                                </td>
                                <td>
                                    <span className={`notification-badge notification-badge--type notification-badge--${notification.type}`}>
                                        {notification.type}
                                    </span>
                                </td>
                                <td>
                                    <span className={`notification-badge notification-badge--importance notification-badge--${notification.importance}`}>
                                        {notification.importance}
                                    </span>
                                </td>
                                <td>
                                    <span className="notification-badge notification-badge--target">
                                        {t(notification.target_audience)}
                                    </span>
                                </td>
                                <td>
                                    <div className="notification-table__date">
                                        {formatDate(notification.sent_at)}
                                        {notification.scheduled_time && !notification.sent_at && (
                                            <small>{t('scheduledFor')} {formatDate(notification.scheduled_time)}</small>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="notification-table__count">
                                        {notification.sent_count || 0}
                                    </div>
                                </td>
                                <td>
                                    <div className="notification-table__rate">
                                        {getDeliveryRate(notification)}
                                    </div>
                                </td>
                                <td>
                                    <span className={`notification-badge notification-badge--status notification-badge--${getStatusBadge(notification)}`}>
                                        {getStatusBadge(notification) === 'sent' ? t('sent') : t('scheduled')}
                                    </span>
                                </td>
                                <td>
                                    <div className="notification-actions">
                                        <button
                                            className="notification-action notification-action--view"
                                            title={t('viewDetails')}
                                            onClick={() => {/* Detay modalı aç */ }}
                                        >
                                            <Eye size={14} />
                                        </button>
                                        <button
                                            className="notification-action notification-action--delete"
                                            onClick={() => handleDelete(notification.id)}
                                            title={t('delete')}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {notifications.length === 0 && !loading && (
                    <div className="notification-empty">
                        <div className="notification-empty__icon">
                            <Bell size={48} />
                        </div>
                        <h3>{t('noNotifications')}</h3>
                        <p>{t('noNotificationsDescription')}</p>
                    </div>
                )}
            </div>

            {/* Sayfalama */}
            {notifications.length > 0 && (
                <div className="notification-pagination">
                    <div className="notification-pagination__info">
                        {t('showing')} {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalPages * 20)} {t('of')} {totalPages * 20} {t('notifications')}
                    </div>
                    <div className="notification-pagination__controls">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className="notification-pagination__button"
                        >
                            {t('previous')}
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = i + 1;
                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`notification-pagination__button ${currentPage === page ? 'notification-pagination__button--active' : ''}`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        {totalPages > 5 && (
                            <span className="notification-pagination__ellipsis">...</span>
                        )}

                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="notification-pagination__button"
                        >
                            {t('next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationHistory;